/**
 * Chat Scenario Node Types
 *
 * A `chat_scenario` node holds a whole scripted conversation — an ordered
 * list of turns, each with a user message and per-turn LLM-judge assertions
 * — against a saved Chat Target (`packages/types/src/chat-target.ts`).
 * Mirrors the `api_sequence` pattern: one node, one config, a dedicated
 * simplified editor instead of a general canvas.
 *
 * @see docs/features/chat-evals.md
 */

import { z } from 'zod';
import { ChatAssertionSeveritySchema } from './chat-target.js';

export const ChatScenarioAssertionSchema = z.object({
  id: z.string().min(1),
  rubric: z.string().min(1),
  severity: ChatAssertionSeveritySchema,
});

export type ChatScenarioAssertion = z.infer<typeof ChatScenarioAssertionSchema>;

export const ChatScenarioTurnSchema = z.object({
  id: z.string().min(1),
  message: z.string().min(1),
  assertions: z.array(ChatScenarioAssertionSchema).default([]),
});

export type ChatScenarioTurn = z.infer<typeof ChatScenarioTurnSchema>;

export const ChatScenarioNodeConfigSchema = z.object({
  chatTargetId: z.string().min(1),
  turns: z.array(ChatScenarioTurnSchema).min(1),
});

export type ChatScenarioNodeConfig = z.infer<typeof ChatScenarioNodeConfigSchema>;

/** A single per-invariant verdict from the judge, attached to a turn. */
export interface ChatTranscriptVerdict {
  /** Set when this verdict grades a chat target's global invariant. */
  invariantId?: string;
  /** Set when this verdict grades one of the turn's own assertions. */
  assertionId?: string;
  name: string;
  rubric: string;
  severity: 'warn' | 'fail';
  pass: boolean;
  rationale: string;
  /** Quoted span from the response text, required by the judge prompt on failure. */
  quotedSpan?: string;
}

/** One executed turn's bounded transcript record, persisted in step metadata. */
export interface ChatTranscriptTurn {
  turnId: string;
  message: string;
  response: {
    text: string;
    parts?: unknown[];
  };
  verdicts: ChatTranscriptVerdict[];
  durationMs: number;
}

/**
 * Bounded transcript persisted on the step's `metadataJson.chatTranscript`.
 * Response text and part payloads are truncated at persist time — see
 * `handle-chat-scenario-node.ts` for the caps.
 */
export interface ChatTranscriptResult {
  turns: ChatTranscriptTurn[];
  bootstrap?: {
    conversationId: string;
  };
}
